import { useState } from "react";
import { supabase } from "@/lib/supabase/client";
import { useFocusTrap } from "@/hooks/useFocusTrap";

type Props = {
  onClose: () => void;
};

const QUESTIONS = [
  {
    key: "level",
    label: "How would you describe your cycling level?",
    type: "radio" as const,
    options: ["Beginner", "Intermediate", "Advanced", "Competitive"],
  },
  {
    key: "planning",
    label: "How do you currently plan your training?",
    type: "radio" as const,
    options: ["Coach", "Self-planned", "Follow online plans", "I don't plan"],
  },
  {
    key: "aiTrust",
    label: "Would you trust an AI to build your weekly training plan?",
    type: "radio" as const,
    options: ["Yes", "Maybe, if I could tweak it", "No"],
  },
  {
    key: "features",
    label: "What features would make you most likely to subscribe?",
    type: "checkbox" as const,
    options: [
      "Personalized plans",
      "Device sync (Wahoo/Garmin)",
      "Race preparation",
      "Progress tracking",
      "AI coaching chat",
    ],
  },
  {
    key: "price",
    label: "How much would you pay per month for an app like this?",
    type: "radio" as const,
    options: ["$0", "$1\u2013$3", "$4\u2013$6", "$7\u2013$10", "$10+"],
  },
  {
    key: "rideDays",
    label: "How many days per week do you typically ride?",
    type: "radio" as const,
    options: ["1\u20132", "3\u20134", "5\u20136", "7"],
  },
] as const;

export function SurveyModal({ onClose }: Props) {
  const dialogRef = useFocusTrap<HTMLDivElement>();
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setRadio = (key: string, value: string) =>
    setAnswers((prev) => ({ ...prev, [key]: value }));

  const toggleCheckbox = (key: string, value: string) =>
    setAnswers((prev) => {
      const current = (prev[key] as string[] | undefined) ?? [];
      return {
        ...prev,
        [key]: current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value],
      };
    });

  const canSubmit =
    QUESTIONS.every((q) => {
      const a = answers[q.key];
      if (q.type === "checkbox") return Array.isArray(a) && a.length > 0;
      return typeof a === "string" && a.length > 0;
    }) && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const { error: dbError } = await supabase
        .from("survey_responses" as never)
        .insert({ answers, email: email.trim() || null } as never);
      if (dbError) throw dbError;
      setSubmitted(true);
    } catch (err) {
      console.error("[SurveyModal] insert failed:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ backgroundColor: "rgba(16, 32, 29, 0.70)" }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="survey-title"
        className="relative w-full sm:max-w-lg max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto sm:rounded-2xl"
        style={{
          backgroundColor: "#f3f5f2",
          borderColor: "#d7dfdb",
          borderWidth: 1,
          borderStyle: "solid",
          boxShadow: "0 20px 60px rgba(16, 32, 29, 0.18)",
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 p-1.5 transition-colors"
          style={{ color: "#52645f", backgroundColor: "transparent" }}
          aria-label="Close survey"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="px-5 py-8 sm:px-8">
          {submitted ? (
            <div className="text-center py-8">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{ backgroundColor: "rgba(0, 107, 95, 0.1)" }}
              >
                <svg className="w-8 h-8" style={{ color: "#006b5f" }} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold mb-3" style={{ color: "#10201d" }}>
                Thanks for your feedback!
              </h2>
              <p className="text-sm leading-relaxed max-w-sm mx-auto" style={{ color: "#52645f" }}>
                We'll use your input to build something worth your time.
                {email.trim() && " We'll notify you when we launch."}
              </p>
              <button
                onClick={onClose}
                className="mt-8 rounded-xl px-6 py-3 text-sm font-bold transition-colors"
                style={{ backgroundColor: "#006b5f", color: "#ffffff" }}
              >
                Back to site
              </button>
            </div>
          ) : (
            <>
              <h2
                id="survey-title"
                className="text-xl font-bold mb-1"
                style={{ color: "#10201d" }}
              >
                Help us build the right app
              </h2>
              <p className="text-sm mb-6" style={{ color: "#52645f" }}>
                6 quick questions — takes under a minute.
              </p>

              <div className="space-y-6">
                {QUESTIONS.map((q) => (
                  <fieldset key={q.key}>
                    <legend className="text-sm font-semibold mb-2.5" style={{ color: "#10201d" }}>
                      {q.label}
                    </legend>
                    <div className="flex flex-wrap gap-2">
                      {q.options.map((opt) => {
                        const isRadio = q.type === "radio";
                        const selected = isRadio
                          ? answers[q.key] === opt
                          : ((answers[q.key] as string[] | undefined) ?? []).includes(opt);
                        return (
                          <button
                            key={opt}
                            type="button"
                            onClick={() =>
                              isRadio
                                ? setRadio(q.key, opt)
                                : toggleCheckbox(q.key, opt)
                            }
                            className="rounded-lg px-3.5 py-2 text-sm font-medium transition-all"
                            style={
                              selected
                                ? {
                                    backgroundColor: "#006b5f",
                                    color: "#ffffff",
                                    borderWidth: 1,
                                    borderStyle: "solid",
                                    borderColor: "#006b5f",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                                  }
                                : {
                                    backgroundColor: "#ffffff",
                                    color: "#10201d",
                                    borderWidth: 1,
                                    borderStyle: "solid",
                                    borderColor: "#bdc9c4",
                                  }
                            }
                          >
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  </fieldset>
                ))}

                {/* Email (optional) */}
                <div>
                  <label
                    htmlFor="survey-email"
                    className="text-sm font-semibold mb-2 block"
                    style={{ color: "#10201d" }}
                  >
                    Email{" "}
                    <span className="font-normal" style={{ color: "#52645f" }}>
                      (optional — get notified at launch)
                    </span>
                  </label>
                  <input
                    id="survey-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full rounded-lg px-3.5 py-2.5 text-sm outline-none transition-colors focus:ring-2"
                    style={{
                      backgroundColor: "#ffffff",
                      color: "#10201d",
                      borderWidth: 1,
                      borderStyle: "solid",
                      borderColor: "#bdc9c4",
                    }}
                  />
                </div>
              </div>

              {error && (
                <p role="alert" className="mt-4 text-sm" style={{ color: "#8a4545" }}>
                  {error}
                </p>
              )}

              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="mt-8 w-full rounded-xl px-6 py-3.5 text-sm font-bold transition-all"
                style={
                  canSubmit
                    ? {
                        backgroundColor: "#006b5f",
                        color: "#ffffff",
                        boxShadow: "0 1px 2px rgba(10, 32, 28, 0.18)",
                      }
                    : {
                        backgroundColor: "#bdc9c4",
                        color: "#8a9b95",
                        cursor: "not-allowed",
                      }
                }
              >
                {submitting ? "Sending\u2026" : "Submit"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
